import React, { useRef, useState, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useEditor } from '../../context/EditorContext';
import { RotateCw, MoreHorizontal, Link2, Layers } from 'lucide-react';
import { documentObjects, storyChainOf, locateObject, locationLabel } from '../../lib/storyChain';
import { ObjectRenderer, buildTextStyles } from './ObjectRenderer';
import { ObjectContextMenu } from './ObjectContextMenu';
import { ParagraphMarks } from './ParagraphMarks';
import { measureContentHeight, htmlToText, sanitizeHtml, textToHtml, scaleInlineHtml } from '../../lib/textUtils';
import { scaleLineHeight } from '../../lib/units';
import { getDisplayHtml } from './ObjectRenderer';
import { computeWrapObstacles, obstaclesSignature } from '../../lib/textWrap';
import { layoutTextBox, isEngineEligible, needsEngine, getBaselineGuide } from '../../lib/textLayout';
import { canHoldText } from '../../lib/shapeText';
import { tabOptionsOf } from '../../lib/tabLayout';
import { useFontsVersion } from '../../hooks/useFontsVersion';
import { hasWordArtTransform } from '../../lib/wordart';
import { storyPlainText, storyTailPatch, linkedDisplay, editWindowFor, joinStoryHtml, joinStoryWindow, flowHtmlKey, flowHtmlOf, richFlowFor } from '../../lib/storyEdit';
import { measureBoxFlow, liveFlowOf } from '../../lib/storyFlow';
import { editingStore } from '../../lib/editingStore';
import { snapObjectMove, snapObjectResize } from '../../lib/objectSnap';
import { toast } from 'sonner';

export const CanvasObject = ({ object, isSelected, zoom, onAlignmentGuides, canvasWidth, canvasHeight, allObjects = [], userGuides = [], isMasterObject = false, isScratchObject = false }) => {
  const { dispatch, rawDispatch, state, openDialog } = useEditor();
  const objectRef = useRef(null);
  const contentRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [rotationStart, setRotationStart] = useState({ angle: 0, startAngle: 0 });
  const [overflowing, setOverflowing] = useState(false);
  const [fitScale, setFitScale] = useState(1);
  const isEditing = state.editingObjectId === object.id;
  const props = useMemo(() => object.properties || {}, [object.properties]);
  const isText = object.type === 'textbox' || object.type === 'text';
  const locked = object.locked || isMasterObject;

  /* ---------------- snapping ---------------- */
  // Align To Guides (ruler / margin / column / fold / panel guides), Align To Objects (page centre / edges, other objects),
  // Snap To Grid — lib/objectSnap does the maths for moves and resizes and names what each axis locked to (the readout).
  const snapCtx = useMemo(() => ({
    guides: userGuides || [], pageWidth: canvasWidth || 816, pageHeight: canvasHeight || 1056,
    objects: allObjects, skipIds: state.selectedObjectIds.includes(object.id) ? state.selectedObjectIds : [object.id, ...state.selectedObjectIds],
    snapToGuides: !!state.snapToGuides, alignToObjects: !!state.showAlignmentGuides, snapToGrid: !!state.snapToGrid, gridSpacing: state.gridSpacing, zoom,
  }), [userGuides, canvasWidth, canvasHeight, allObjects, state.selectedObjectIds, object.id, state.snapToGuides, state.showAlignmentGuides, state.snapToGrid, state.gridSpacing, zoom]);

  /* ---------------- mouse interactions ---------------- */
  const handleMouseDown = (e) => {
    if (e.button === 2) {
      if (!isSelected) dispatch({ type: 'SELECT_OBJECT', payload: { id: object.id, multi: false } });
      return;
    }
    if (isMasterObject) return;
    if (state.pendingLink && isText && state.pendingLink !== object.id) {
      // Link mode (Publisher): the story may only continue into an empty, unlinked box — on any page.
      e.stopPropagation();
      if (props.linkPrevId) { toast.error('That text box is already part of a story', { id: 'link-mode', description: 'Choose an empty text box that is not linked yet.' }); return; }
      if (storyPlainText(props).trim()) { toast.error('Choose an empty text box', { id: 'link-mode', description: 'Text can only flow into a box with nothing in it. Delete its text first, or draw a new box.' }); return; }
      const all = documentObjects(state.pages, state.scratch);
      if (storyChainOf(state.pendingLink, all).some((o) => o.id === object.id)) { toast.error('That box is already in this story', { id: 'link-mode' }); return; }
      const fromLoc = locateObject(state.pendingLink, state.pages, state.scratch);
      dispatch({ type: 'LINK_TEXTBOXES', payload: { fromId: state.pendingLink, toId: object.id } });
      rawDispatch({ type: 'SET_PENDING_LINK', payload: null });
      const crossPage = fromLoc && !fromLoc.scratch && fromLoc.pageIndex !== state.currentPageIndex;
      toast.success('Text boxes linked', { id: 'link-mode', description: crossPage ? `The story on ${locationLabel(fromLoc, state.pages)} now continues in this box.` : 'Overflow text now flows into this box.' });
      return;
    }
    if (state.activeTool !== 'select') return;
    e.stopPropagation();
    if (locked) {
      if (!isSelected) dispatch({ type: 'SELECT_OBJECT', payload: { id: object.id, multi: e.shiftKey } });
      return;
    }
    if (!isSelected || e.shiftKey) dispatch({ type: 'SELECT_OBJECT', payload: { id: object.id, multi: e.shiftKey } });
    if (isEditing) return;
    const selectedNow = e.shiftKey ? [...state.selectedObjectIds, object.id] : isSelected ? state.selectedObjectIds : [object.id];
    const startPositions = allObjects.filter((o) => selectedNow.includes(o.id)).map((o) => ({ id: o.id, x: o.x, y: o.y }));
    setDragStart({ mouseX: e.clientX, mouseY: e.clientY, x: object.x, y: object.y, others: startPositions });
    setIsDragging(true);
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging && dragStart) {
      const dx = (e.clientX - dragStart.mouseX) / zoom;
      const dy = (e.clientY - dragStart.mouseY) / zoom;
      // Alt: move freely, no snapping (Publisher)
      const r = snapObjectMove({ x: dragStart.x + dx, y: dragStart.y + dy, width: object.width, height: object.height }, { ...snapCtx, free: e.altKey });
      const adjX = r.x - dragStart.x, adjY = r.y - dragStart.y;
      if (dragStart.others.length > 1) {
        dispatch({ type: 'UPDATE_OBJECTS', payload: dragStart.others.map((o) => ({ id: o.id, data: { x: o.x + adjX, y: o.y + adjY } })) });
      } else {
        dispatch({ type: 'UPDATE_OBJECT', payload: { id: object.id, data: { x: r.x, y: r.y } } });
      }
      onAlignmentGuides && onAlignmentGuides(r.guides);
      rawDispatch({ type: 'SET_OBJECT_DRAG', payload: { id: object.id, mode: 'move', x: r.x, y: r.y, width: object.width, height: object.height, snaps: r.snaps, count: Math.max(1, dragStart.others.length) } });
    }
    if (isResizing && resizeHandle && dragStart) {
      const dx = (e.clientX - dragStart.mouseX) / zoom;
      const dy = (e.clientY - dragStart.mouseY) / zoom;
      const keepRatio = e.shiftKey || object.type === 'image' || (props.lockAspect && object.type !== 'textbox');
      // the dragged edges snap like a moved object does; Alt resizes from the centre and, as everywhere, switches snapping off
      const r = snapObjectResize(dragStart, resizeHandle, dx, dy, { keepRatio, fromCentre: e.altKey, free: e.altKey }, snapCtx);
      dispatch({ type: 'UPDATE_OBJECT', payload: { id: object.id, data: { x: r.x, y: r.y, width: r.width, height: r.height } } });
      onAlignmentGuides && onAlignmentGuides(r.guides);
      rawDispatch({ type: 'SET_OBJECT_DRAG', payload: { id: object.id, mode: 'resize', x: r.x, y: r.y, width: r.width, height: r.height, snaps: r.snaps, count: 1 } });
    }
  }, [isDragging, isResizing, resizeHandle, dragStart, zoom, object, dispatch, rawDispatch, onAlignmentGuides, snapCtx, props.lockAspect]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
    setDragStart(null);
    onAlignmentGuides && onAlignmentGuides([]);
    rawDispatch({ type: 'SET_OBJECT_DRAG', payload: null });
  }, [onAlignmentGuides, rawDispatch]);

  const handleRotationMove = useCallback((e) => {
    if (!isRotating || !objectRef.current) return;
    const rect = objectRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
    const current = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
    let rot = rotationStart.angle + (current - rotationStart.startAngle);
    rot = ((rot % 360) + 360) % 360;
    if (e.shiftKey) rot = Math.round(rot / 15) * 15;
    for (const s of [0, 90, 180, 270, 360]) if (Math.abs(rot - s) < 4) rot = s % 360;
    dispatch({ type: 'UPDATE_OBJECT', payload: { id: object.id, data: { rotation: rot } } });
  }, [isRotating, rotationStart, object.id, dispatch]);
  const handleRotationEnd = useCallback(() => setIsRotating(false), []);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    }
    return undefined;
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);
  useEffect(() => {
    if (isRotating) {
      window.addEventListener('mousemove', handleRotationMove);
      window.addEventListener('mouseup', handleRotationEnd);
      return () => { window.removeEventListener('mousemove', handleRotationMove); window.removeEventListener('mouseup', handleRotationEnd); };
    }
    return undefined;
  }, [isRotating, handleRotationMove, handleRotationEnd]);
  // Let floating UI (picture mini toolbar) hide while this object is being moved / resized / rotated.
  const interacting = isDragging || isResizing || isRotating;
  useEffect(() => {
    if (!interacting || !isSelected) return undefined;
    rawDispatch({ type: 'SET_INTERACTING', payload: object.id });
    return () => rawDispatch({ type: 'SET_INTERACTING', payload: null });
  }, [interacting, isSelected, object.id, rawDispatch]);

  const handleResizeStart = (e, handle) => {
    if (locked) return;
    e.stopPropagation();
    e.preventDefault();
    setDragStart({ mouseX: e.clientX, mouseY: e.clientY, x: object.x, y: object.y, width: object.width, height: object.height, others: [] });
    setIsResizing(true);
    setResizeHandle(handle);
  };
  const handleRotationStart = (e) => {
    if (locked) return;
    e.stopPropagation();
    e.preventDefault();
    const rect = objectRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
    setIsRotating(true);
    setRotationStart({ angle: object.rotation || 0, startAngle: Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI) });
  };

  const handleDoubleClick = (e) => {
    if (isMasterObject) {
      toast.info('This object belongs to the master page.', { id: 'master-object-hint', description: 'Open the master page to move, edit or delete it.', action: { label: 'Edit Master Page', onClick: () => dispatch({ type: 'EDIT_MASTER', payload: {} }) }, duration: 6000 });
      return;
    }
    e.stopPropagation();
    if (isText) {
      // Linked continuation boxes are editable too: the edits are written back into the shared story (see onEdit).
      // The caret goes where the user double-clicked (ObjectRenderer's editing effect, editingStore.placeCaretAtPoint).
      editingStore.pendingPoint = { objectId: object.id, x: e.clientX, y: e.clientY };
      rawDispatch({ type: 'SET_EDITING', payload: object.id });
    } else if (object.type === 'table') {
      rawDispatch({ type: 'SET_EDITING', payload: object.id });
    } else if (object.type === 'image') {
      if (props.src) dispatch({ type: 'SHOW_PHOTO_EDITOR', payload: object });
      else dispatch({ type: 'SHOW_IMAGE_GALLERY', payload: { mode: 'replace' } });
    } else if (object.type === 'shape') {
      // Publisher: double-clicking a shape puts the insertion point in it (Edit Text); lines open the Format dialog
      if (canHoldText(object)) { editingStore.pendingPoint = { objectId: object.id, x: e.clientX, y: e.clientY }; rawDispatch({ type: 'SET_EDITING', payload: object.id }); }
      else openDialog('format-object', { objectId: object.id, tab: 'colors' });
    } else if (object.type === 'group') {
      // Building blocks are inserted as groups: double-click opens them up for editing the parts.
      dispatch({ type: 'SELECT_OBJECT', payload: { id: object.id } });
      dispatch({ type: 'UNGROUP_OBJECTS' });
      toast.info('Ungrouped — edit the parts, then select them and press Ctrl+G to group again.', { id: 'ungroup-hint' });
    }
  };

  /* ---------------- text editing sync ---------------- */
  const lastHtmlRef = useRef(null);
  // the whole publication: a story's head may sit on another page (or in the scratch area)
  const stateRef = useRef(state);
  stateRef.current = state;
  const docObjects = () => documentObjects(stateRef.current.pages, stateRef.current.scratch);
  // Linked story editing (Publisher: every box is a window on one story). Computed once when editing starts, in the
  // same render, so the editable opens with exactly this box's share of the story: the head keeps its formatted
  // prefix and the formatted tail is re-attached after every edit; a continuation box holds the text flowing into it
  // minus what flows on (formatted when the story is), and writes prefix + edit + tail back into the head.
  const editWindow = useMemo(() => {
    if (!isEditing || !isText || !(props.linkPrevId || props.linkNextId)) return null;
    return editWindowFor(object, props.linkPrevId ? findHead(object, docObjects()) : null, state.textFlow);
    // a snapshot taken when editing starts (see above) — the object / flow of later renders must not reopen the window
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);
  const editWindowRef = useRef(null);
  editWindowRef.current = editWindow;
  useEffect(() => { if (!isEditing) lastHtmlRef.current = null; }, [isEditing]);
  // the latest render's values for callbacks that must not go stale (onEdit is memoised on the object id); filled in
  // below, once the wrap obstacles are known
  const latestRef = useRef({ object, props, obstacles: [], engineOn: false });

  /**
   * Continue editing in another box of the story (Publisher: the caret follows the text into the next box, Backspace
   * at the start of a box goes back to the one before, Ctrl+Home / Ctrl+End reach the story's first / last box).
   * `offset` is the caret's plain-text offset inside the target's window (Infinity: at its end).
   */
  const handOff = useCallback((targetId, offset) => {
    if (!targetId) return;
    const st = stateRef.current;
    if (!documentObjects(st.pages, st.scratch).some((o) => o.id === targetId)) return;
    editingStore.pendingCaret = { objectId: targetId, offset };
    dispatch({ type: 'SELECT_OBJECT_ANYWHERE', payload: targetId }); // switches the page when the box sits elsewhere
    rawDispatch({ type: 'SET_EDITING', payload: targetId });
  }, [dispatch, rawDispatch]);
  const onHandOff = useCallback((dir) => {
    const { props: p, object: o } = latestRef.current;
    const objs = docObjects();
    if (dir === 'prev') return p.linkPrevId ? (handOff(p.linkPrevId, Infinity), true) : false;
    if (dir === 'first') { const head = findHead(o, objs); return head && head.id !== o.id ? (handOff(head.id, 0), true) : false; }
    if (dir === 'last') { const chain = storyChainOf(o.id, objs); const last = chain[chain.length - 1]; return last && last.id !== o.id ? (handOff(last.id, Infinity), true) : false; }
    return false;
  }, [handOff]);

  /**
   * Live flow while this linked box is edited (storyFlow.liveFlowOf): the words the editable no longer holds appear in
   * the next box at once; when the caret itself is past the box's room, editing continues there (the typed text keeps
   * flowing, as in Publisher). The editable shows its text clipped at the box — never scrolled — so no word is ever on
   * screen twice.
   */
  const liveFlow = (w, storyHtml, windowHtml, caret) => {
    const { object: o, props: p, obstacles: obs, engineOn: eng } = latestRef.current;
    if (!w || (!p.linkNextId && !p.linkPrevId)) return;
    const live = liveFlowOf(w, o, storyHtml, windowHtml, { obstacles: eng ? obs : [] });
    // words that fit back into the box (the room a deletion left) return to the editable at once — and the window's
    // tail bookkeeping moves on past them, so the next edit is joined to the right tail
    if (live.pull && editingStore.objectId === o.id && editingStore.appendHtml) {
      const { fragmentHtml, midBlock, joiner, tail } = live.pull;
      if (editingStore.appendHtml(fragmentHtml, midBlock, joiner, { justifyLast: !!tail.midBlock && (p.textAlign || 'left') === 'justify' })) {
        if (w.kind === 'head') Object.assign(w, { tailHtml: tail.tailHtml || '', midBlock: !!tail.midBlock, joiner: tail.joiner || ' ' });
        else if (w.rich) Object.assign(w.rich, { tailHtml: tail.tailHtml || '', mid2: !!tail.midBlock, joiner2: tail.joiner || ' ' });
        else Object.assign(w, { tailPlain: tail.tailPlain || '', joiner: tail.joiner || '' });
        lastHtmlRef.current = null; // the editable changed under the last sync
      }
    }
    const tf = stateRef.current.textFlow;
    if ((tf[o.id] || '') !== live.overflow || flowHtmlOf(tf, o.id) !== live.overflowHtml) rawDispatch({ type: 'SET_TEXT_FLOW', payload: { id: o.id, overflow: live.overflow, overflowHtml: live.overflowHtml } });
    // the editable clips the words past the box's room (ObjectRenderer.TextContent editClipAt) — they show in the next box
    setLiveClipAt(live.overflows && p.linkNextId ? live.clipAt : null);
    if (live.overflows && p.linkNextId && caret !== null && caret !== undefined && caret >= live.fitLen) handOff(p.linkNextId, caret - live.fitLen);
  };
  const [liveClipAt, setLiveClipAt] = useState(null);
  useEffect(() => { if (!isEditing) setLiveClipAt(null); }, [isEditing]);
  // when editing starts, the box's outgoing flow is brought up to date (words handed over from the box before may push
  // this box's own words on to the next one)
  useEffect(() => {
    const w = editWindowRef.current;
    if (!isEditing || !w) return;
    const o = latestRef.current.object;
    const head = w.kind === 'head' ? o : docObjects().find((x) => x.id === w.headId);
    const hp = head?.properties || {};
    const storyHtml = hp.html || textToHtml(hp.text || '');
    const windowHtml = w.kind === 'head' ? w.prefixHtml : w.rich ? w.rich.windowHtml : textToHtml(w.windowText || '');
    liveFlow(w, storyHtml, windowHtml, null);
    // runs when editing starts only; liveFlow / docObjects read the latest render through refs, never a stale closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const onEdit = useCallback((html, _text, caret) => {
    if (html === lastHtmlRef.current) return;
    lastHtmlRef.current = html;
    const clean = sanitizeHtml(html);
    const w = editWindowRef.current;
    if (w?.kind === 'head') {
      // the edited window followed by the untouched, formatted tail (no tail: the window is the whole story)
      const merged = w.tailHtml ? joinStoryHtml(clean, w.tailHtml, w.midBlock, w.joiner) : clean;
      dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [object.id], props: { html: merged, text: htmlToText(merged) } } });
      liveFlow(w, merged, clean, caret);
      return;
    }
    if (w?.kind === 'cont') {
      const head = docObjects().find((o) => o.id === w.headId);
      if (head) {
        // the head may be on another page: update it wherever it is (never this box's own text — the box is a window)
        let patch;
        if (w.rich) {
          // formatted story: the untouched parts before and after this window keep their formatting, so does the window
          const merged = joinStoryWindow(w.rich, clean);
          patch = { html: merged, text: htmlToText(merged) };
        } else {
          const edited = htmlToText(clean);
          const restNew = w.tailPlain ? edited.replace(/\s+$/, '') + (w.joiner || ' ') + w.tailPlain : edited;
          patch = storyTailPatch(head.properties || {}, w.prefix, restNew);
        }
        dispatch({ type: 'UPDATE_OBJECT_PROPS_BY_ID', payload: { ids: [head.id], props: patch } });
        liveFlow(w, patch.html || textToHtml(patch.text || ''), clean, caret);
      }
      return;
    }
    const text = htmlToText(clean);
    dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [object.id], props: { html: clean, text } } });
    // memoised on the object id so the editable's sync callback stays the same for the whole editing session; everything
    // else (window, object, flow helpers) is read through refs at call time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, object.id]);

  useEffect(() => {
    if (!isEditing) return undefined;
    const stop = (e) => {
      if (objectRef.current && !objectRef.current.contains(e.target) && !e.target.closest('[data-ribbon], [data-dialog], [role="dialog"], [role="menu"], [data-radix-popper-content-wrapper], [data-keep-editing]')) {
        rawDispatch({ type: 'SET_EDITING', payload: null });
      }
    };
    window.addEventListener('mousedown', stop, true);
    return () => window.removeEventListener('mousedown', stop, true);
  }, [isEditing, rawDispatch]);

  // (r, c, text) for a single cell, or an array of [r, c, text] triples to commit several cells in one update.
  const onCellChange = useCallback((r, c, text) => {
    const changes = Array.isArray(r) ? r : [[r, c, text]];
    const cells = (props.cells || []).map((row) => row.map((cell) => ({ ...cell })));
    let changed = false;
    changes.forEach(([rr, cc, tt]) => {
      while (cells.length <= rr) cells.push([]);
      while (cells[rr].length <= cc) cells[rr].push({ text: '' });
      if ((cells[rr][cc].text || '') === tt) return;
      cells[rr][cc] = { ...cells[rr][cc], text: tt };
      changed = true;
    });
    if (!changed) return;
    dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [object.id], props: { cells } } });
  }, [dispatch, object.id, props.cells]);

  /* ---------------- linked text / overflow / autofit ---------------- */
  const textOverride = isText && props.linkPrevId ? (state.textFlow[props.linkPrevId] ?? '') : undefined;
  // the formatted form of the incoming text, when the previous box recorded one that matches it (lib/storyEdit.js)
  const flowHtmlIn = isText && props.linkPrevId ? state.textFlow[flowHtmlKey(props.linkPrevId)] || '' : '';
  const incomingHtml = useMemo(() => (flowHtmlIn ? richFlowFor(flowHtmlIn, textOverride) : ''), [flowHtmlIn, textOverride]);
  const fullPlainText = useMemo(() => {
    if (!isText) return '';
    if (textOverride !== undefined) return textOverride;
    return props.html ? htmlToText(sanitizeHtml(props.html)) : props.text || '';
  }, [isText, textOverride, props.html, props.text]);

  const pads = useMemo(() => ({
    t: props.paddingTop ?? props.padding ?? 4, r: props.paddingRight ?? props.padding ?? 4, b: props.paddingBottom ?? props.padding ?? 4, l: props.paddingLeft ?? props.padding ?? 4,
  }), [props.paddingTop, props.paddingRight, props.paddingBottom, props.paddingLeft, props.padding]);

  /* ---------------- wrap obstacles + live layout ---------------- */
  const obstaclesRaw = isText ? computeWrapObstacles(object, allObjects, pads) : [];
  const obstaclesSig = obstaclesSignature(obstaclesRaw);
  // obstaclesRaw is recomputed every render; its identity is pinned to the signature so the layout below only re-runs
  // when an obstacle actually moved
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const obstacles = useMemo(() => obstaclesRaw, [obstaclesSig]);
  const fontsVersion = useFontsVersion();
  // The engine also runs without obstacles for custom tab stops, Align to Baselines and rotated (vertical) text.
  const engineOn = isText && (obstacles.length > 0 || needsEngine(props)) && isEngineEligible(props);
  latestRef.current = { object, props, obstacles, engineOn };
  const originY = object.y + pads.t;
  const baselineKey = props.alignToBaselines ? `${Math.round(originY)}:${JSON.stringify(getBaselineGuide(state.guides, state.viewOptions?.baselineSpacing))}` : '';
  // what the box lays out: a continuation gets the text flowing in — formatted when the previous box recorded it so
  const displayHtml = useMemo(() => (isText ? (textOverride !== undefined ? incomingHtml || textToHtml(textOverride) : getDisplayHtml(props, undefined)) : ''), [isText, textOverride, incomingHtml, props]);
  const wrapLayout = useMemo(() => {
    if (!engineOn || isEditing) return null;
    const merged = buildTextStyles(props, 1).props;
    return layoutTextBox(merged, displayHtml, object.width - pads.l - pads.r, object.height - pads.t - pads.b, obstacles, { fontScale: fitScale, originY });
    // pads derive from props (listed); originY (object.y) only matters for Align to Baselines, where baselineKey carries
    // it — moving a box must not re-run the layout otherwise
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineOn, isEditing, props, displayHtml, object.width, object.height, obstacles, fitScale, fontsVersion, baselineKey]);

  useLayoutEffect(() => {
    if (!isText) return;
    if (hasWordArtTransform(props)) {
      // WordArt transforms stretch the text to the frame: no overflow, autofit or linked flow.
      if (fitScale !== 1) setFitScale(1);
      if (state.textFlow[object.id]) rawDispatch({ type: 'SET_TEXT_FLOW', payload: { id: object.id, overflow: '' } });
      setOverflowing(false);
      return;
    }
    const availW = Math.max(1, object.width - pads.l - pads.r - (props.indentLeft || 0) - (props.indentRight || 0));
    const availH = Math.max(1, object.height - pads.t - pads.b);
    const { text: style, props: merged } = buildTextStyles(props, 1);
    const measureStyle = { ...style, transform: undefined, width: undefined };
    const htmlForMeasure = textOverride !== undefined ? incomingHtml || textToHtml(textOverride) : getDisplayHtml(props, undefined);
    const autofit = props.autofit || 'none';
    const useEngine = engineOn && !isEditing;
    const engineAt = (s) => layoutTextBox(merged, htmlForMeasure, object.width - pads.l - pads.r, availH, obstacles, { fontScale: s, originY });
    const tabs = tabOptionsOf(merged, 1); // tab stops in box px — the measure element is laid out at box scale
    const measureAt = (s, h) => measureContentHeight({ ...measureStyle, fontSize: style.fontSize * s, lineHeight: scaleLineHeight(style.lineHeight, s) }, scaleInlineHtml(h ?? htmlForMeasure, s), availW, tabs);

    if (autofit === 'shrink' || autofit === 'best') {
      const fits = (s) => (useEngine ? engineAt(s).fits : measureAt(s) <= availH + 0.5);
      let lo = 0.2, hi = autofit === 'best' ? 4 : 1;
      if (fits(hi)) { setFitScale(hi); }
      else {
        for (let i = 0; i < 14; i++) { const mid = (lo + hi) / 2; if (fits(mid)) lo = mid; else hi = mid; }
        setFitScale(Math.round(lo * 100) / 100);
      }
    } else if (fitScale !== 1) {
      setFitScale(1);
    }

    const scaleNow = autofit === 'shrink' || autofit === 'best' ? fitScale : 1;
    let isOver;
    let engineResult = null;
    if (useEngine) {
      engineResult = engineAt(scaleNow);
      isOver = !engineResult.fits;
    } else {
      const contentH = measureAt(scaleNow);
      isOver = contentH > availH + 0.5;
    }

    if (autofit === 'grow' && isOver && !isEditing) {
      let needed;
      if (useEngine) {
        // smallest height at which the wrapped text fits
        let lo = availH, hi = availH + 4000;
        for (let i = 0; i < 14; i++) { const mid = (lo + hi) / 2; if (layoutTextBox(merged, htmlForMeasure, object.width - pads.l - pads.r, mid, obstacles, { fontScale: scaleNow, originY }).fits) hi = mid; else lo = mid; }
        needed = hi + pads.t + pads.b;
      } else {
        needed = measureAt(scaleNow) + pads.t + pads.b;
      }
      if (Math.abs(needed - object.height) > 1) dispatch({ type: 'UPDATE_OBJECT', payload: { id: object.id, data: { height: Math.ceil(needed) } } });
    }

    // While this box is being edited its flow stays frozen (the editable holds only this box's window on the story;
    // the words it pushes out or pulls back move when editing ends) — otherwise they would show here and in the next
    // box at the same time.
    if (isEditing && (props.linkNextId || props.linkPrevId)) return;
    if (props.linkNextId || props.linkPrevId) {
      // The one measurement every linked box uses (storyFlow.measureBoxFlow — the off-page pass measures boxes on
      // other pages with it too), so the split never moves when a page is opened. It records what flows on to the next
      // box (plain, and formatted when the story is), and for the last box what does not fit (the Story Map reports it).
      const { overflow: flow, overflowHtml } = measureBoxFlow(object, fullPlainText, { obstacles: useEngine ? obstacles : [], incomingHtml });
      if ((state.textFlow[object.id] || '') !== flow || flowHtmlOf(state.textFlow, object.id) !== overflowHtml) rawDispatch({ type: 'SET_TEXT_FLOW', payload: { id: object.id, overflow: flow, overflowHtml } });
      setOverflowing(!props.linkNextId && isOver && !(autofit === 'shrink' || autofit === 'best'));
      return;
    }
    if (state.textFlow[object.id]) rawDispatch({ type: 'SET_TEXT_FLOW', payload: { id: object.id, overflow: '' } });
    setOverflowing(isOver && !(autofit === 'shrink' || autofit === 'best'));
    // deliberately keyed on what changes the measurement: this effect *writes* state.textFlow (listing it would make every
    // box re-measure on every other box's result), moving the box (object.x / y) must not re-measure, pads / originY
    // derive from props / baselineKey, and dispatch / rawDispatch are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isText, fullPlainText, incomingHtml, props, object.width, object.height, textOverride, isEditing, fitScale, engineOn, obstacles, fontsVersion, baselineKey]);

  /* ---------------- render ---------------- */
  // What a linked box shows on screen: never the words that flow on to the next box (measurement above stays on the
  // full text). While editing, the editable holds the text itself.
  const outgoingFlow = isText && props.linkNextId ? state.textFlow[object.id] || '' : '';
  // keyed on the three flow strings this box reads (textOverride, incomingHtml, outgoingFlow), not the whole textFlow map
  const linkedRender = useMemo(() => (isText && (props.linkPrevId || props.linkNextId) && !isEditing ? linkedDisplay(object, state.textFlow) : null), [isText, props.linkPrevId, props.linkNextId, isEditing, object, textOverride, incomingHtml, outgoingFlow]); // eslint-disable-line react-hooks/exhaustive-deps
  // editing a linked box: the editable opens with this box's window (head: formatted prefix; continuation: its share — formatted when the story is)
  const renderTextOverride = editWindow ? (editWindow.kind === 'cont' && !editWindow.rich ? editWindow.windowText : undefined) : linkedRender ? linkedRender.textOverride : textOverride;
  const renderHtmlOverride = editWindow ? (editWindow.kind === 'head' ? editWindow.prefixHtml : editWindow.rich ? editWindow.rich.windowHtml || '<div><br></div>' : undefined) : linkedRender ? linkedRender.htmlOverride : undefined;
  const resizeHandles = ['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se'];
  const handleStyle = (h) => {
    const size = 9, off = -size / 2;
    const base = { width: size, height: size, position: 'absolute', background: 'white', border: '2px solid hsl(262 83% 58%)', borderRadius: 2, cursor: `${h}-resize`, zIndex: 5 };
    const pos = { nw: { top: off, left: off }, n: { top: off, left: '50%', marginLeft: off }, ne: { top: off, right: off }, w: { top: '50%', left: off, marginTop: off }, e: { top: '50%', right: off, marginTop: off }, sw: { bottom: off, left: off }, s: { bottom: off, left: '50%', marginLeft: off }, se: { bottom: off, right: off } };
    return { ...base, ...pos[h] };
  };

  // Font picker hover preview: a CSS override on this object (see index.css [data-font-preview]) — the document is untouched.
  const fontPreviewFamily = state.fontPreview && state.fontPreview.ids.includes(object.id) ? state.fontPreview.family : null;
  const showBoundary = state.viewOptions?.boundaries !== false && isText && !isSelected && !props.background?.type?.match(/solid|gradient|image/) && !(props.stroke?.width > 0);
  const cursor = isMasterObject ? 'default' : locked ? 'not-allowed' : isEditing ? 'text' : isDragging ? 'grabbing' : state.pendingLink && isText ? 'copy' : 'move';

  const inner = (
    <div
      ref={objectRef}
      className={`absolute wp-object ${isSelected ? 'object-selected' : ''} ${object.locked ? 'opacity-90' : ''} ${showBoundary ? 'wp-boundary' : ''}`}
      style={{ left: object.x * zoom, top: object.y * zoom, width: object.width * zoom, height: object.height * zoom, transform: `rotate(${object.rotation || 0}deg)`, cursor, zIndex: object.layer, pointerEvents: isMasterObject && !state.viewOptions?.masterSelectable ? 'none' : 'auto', '--wp-font-preview': fontPreviewFamily ? `"${fontPreviewFamily}"` : undefined }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={() => rawDispatch({ type: 'SET_HOVERED_OBJECT', payload: object.id })}
      onMouseLeave={() => rawDispatch({ type: 'SET_HOVERED_OBJECT', payload: null })}
      data-testid={`canvas-object-${object.id}`}
      data-object-type={object.type}
      data-master-object={isMasterObject ? '1' : undefined}
      data-scratch-object={isScratchObject ? '1' : undefined}
      data-font-preview={fontPreviewFamily ? '1' : undefined}
    >
      <ObjectRenderer object={object} scale={zoom} editing={isEditing} onEdit={onEdit} onExitEdit={() => rawDispatch({ type: 'SET_EDITING', payload: null })} onHandOff={onHandOff} editClipAt={isEditing ? liveClipAt : null} textOverride={renderTextOverride} htmlOverride={renderHtmlOverride} obstacles={obstacles} layout={wrapLayout} contentRef={contentRef} fitScale={fitScale} onCellChange={onCellChange} specialChars={!!state.viewOptions?.specialChars && isText} />

      {/* ¶ beside the paragraphs that carry their own spacing (Phase 50 paragraph line spacing) — while selected or edited */}
      {isText && (isSelected || isEditing) && !isMasterObject && (
        <ParagraphMarks objectRef={objectRef} contentRef={contentRef} box={props} editing={isEditing} zoom={zoom} width={object.width} height={object.height} rotation={object.rotation || 0} html={props.html} />
      )}

      {/* scratch-area badge: this object lives beside the page and shows next to every page (Publisher's scratch area) */}
      {isScratchObject && isSelected && !isEditing && (
        <div className="absolute -top-7 left-0 h-5 px-1.5 rounded-sm bg-muted text-muted-foreground border border-border text-[10px] leading-5 whitespace-nowrap shadow-sm pointer-events-none flex items-center gap-1" style={{ transform: `scale(${1 / Math.max(zoom, 0.01)})`, transformOrigin: 'bottom left' }} title="Scratch area: shown beside every page, never printed. Drag it onto a page to make it part of that page." data-testid="scratch-object-badge">
          <Layers className="w-3 h-3" aria-hidden="true" /> Scratch area · all pages
        </div>
      )}

      {/* hyperlink badge */}
      {props.hyperlink && !isEditing && (
        <div className="absolute -top-2 -left-2 h-4 w-4 rounded-sm bg-accent text-accent-foreground flex items-center justify-center shadow-sm" title={props.hyperlink}><Link2 className="w-3 h-3" /></div>
      )}

      {/* overflow indicator */}
      {isText && overflowing && !isEditing && !props.linkNextId && (
        <button
          type="button"
          className="absolute -bottom-2 -right-2 h-5 w-5 rounded-sm bg-primary text-primary-foreground text-[12px] leading-5 text-center shadow-sm flex items-center justify-center hover:bg-primary/90 z-10"
          title="Text is in overflow. Click to autoflow onto new pages or link to another text box."
          onMouseDown={(e) => { e.stopPropagation(); }}
          onClick={(e) => { e.stopPropagation(); openDialog('autoflow', { objectId: object.id }); }}
          data-testid="textbox-overflow-indicator"
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
      )}
      {isText && props.linkNextId && isSelected && (
        <div className="absolute -bottom-2 -right-2 h-4 w-4 rounded-sm bg-emerald-500 text-white flex items-center justify-center shadow-sm" title="Linked to next text box"><Link2 className="w-3 h-3" /></div>
      )}
      {isText && props.linkPrevId && isSelected && (
        <div className="absolute -top-2 -left-2 h-4 w-4 rounded-sm bg-emerald-500 text-white flex items-center justify-center shadow-sm" title="Continues from previous text box"><Link2 className="w-3 h-3" /></div>
      )}

      {/* While editing wrap points / shape points only the mode's own handles are shown (Publisher behaviour). */}
      {isSelected && !locked && !isEditing && state.wrapEditId !== object.id && state.pointEditId !== object.id && state.cropEditId !== object.id && (
        <>
          {resizeHandles.map((h) => <div key={h} style={handleStyle(h)} onMouseDown={(e) => handleResizeStart(e, h)} data-testid={`resize-handle-${h}`} />)}
          <div style={{ position: 'absolute', top: -34, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: isRotating ? 'grabbing' : 'grab', zIndex: 5 }} onMouseDown={handleRotationStart} data-testid="rotation-handle">
            <div style={{ width: 1, height: 16, backgroundColor: 'hsl(262 83% 58%)' }} />
            <div style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: 'white', border: '2px solid hsl(262 83% 58%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><RotateCw size={10} color="#7c3aed" /></div>
          </div>
          {isRotating && <div style={{ position: 'absolute', top: -60, left: '50%', transform: 'translateX(-50%)', backgroundColor: '#18181b', color: 'white', padding: '2px 6px', borderRadius: 4, fontSize: 11, whiteSpace: 'nowrap', zIndex: 1000 }}>{Math.round(object.rotation || 0)}°</div>}
        </>
      )}
    </div>
  );

  if (isMasterObject) return inner;
  return <ObjectContextMenu object={object}>{inner}</ObjectContextMenu>;
};

export const findHead = (obj, all) => {
  let cur = obj;
  const seen = new Set();
  while (cur?.properties?.linkPrevId && !seen.has(cur.id)) {
    seen.add(cur.id);
    const prev = all.find((o) => o.id === cur.properties.linkPrevId);
    if (!prev) break;
    cur = prev;
  }
  return cur;
};
